import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AdminTenant } from '@/features/tenants/useTenants';

import { classifyResetError, useResetPassword } from './useResetPassword';
import { useTenantUsers, type TenantUser } from './useTenantUsers';

/**
 * Reset a tenant user's password (slice 17x, picker added slice 52).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 SLICE 52 — THE OPERATOR PICKS A PERSON, NOT A UUID.
 * ─────────────────────────────────────────────────────────────────────────────
 * The original dialog made the operator TYPE the target user's uuid, read from
 * the provisioning record or the audit log, because no committed endpoint listed a
 * tenant's users. That endpoint now exists (`GET /api/admin/tenants/:tenantId/
 * users`, super-admin) — the command-PIN dialog already uses it — so this dialog
 * gets the same picker. The uuid is gone; the confirm names the PERSON.
 *
 * A DISABLED account is listed and labelled, never hidden — silently omitting a
 * person the operator knows exists is how they conclude provisioning is broken.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THREE STAGES, AND THE CONFIRM IS NOT OPTIONAL.
 * ─────────────────────────────────────────────────────────────────────────────
 * form → confirm → done. This logs the target out of every session and changes a
 * customer's credential; a single misclick must not do that. The confirm stage
 * restates the person and tenant before anything is sent.
 *
 * 🔴 THE PASSWORD NEVER OUTLIVES ITS USE. It lives in local state only until the
 * request resolves, then `setPassword('')` clears it. It is never logged, never
 * rendered back, and never put anywhere the DOM can show it.
 */

const INPUT =
  'w-full min-h-10 rounded-lg border border-raad-border bg-transparent px-3 text-[13.5px] outline-none focus:border-raad-primary';

export function ResetPasswordDialog({
  tenant,
  onClose,
}: {
  tenant: AdminTenant;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  // Super-admin only reaches this dialog (opened solely from the super-admin
  // action in `Tenants`), so the query is enabled unconditionally.
  const usersQ = useTenantUsers(tenant.id, true);
  const [userId, setUserId] = useState('');
  const [password, setPassword] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ person: string } | null>(null);
  const reset = useResetPassword();

  const users = Array.isArray(usersQ.data?.users) ? usersQ.data.users : [];
  const selected = users.find((u) => u.id === userId) ?? null;
  const personName = (u: TenantUser) => u.name ?? u.phone;
  // Mirrors the server floor: a chosen user and a password of at least 12.
  const ready = selected !== null && password.length >= 12;

  async function submit() {
    if (!selected) return;
    setError(null);
    try {
      await reset.mutateAsync({ userId: selected.id, password });
      // 🔴 Clear the plaintext the instant it is no longer needed.
      setPassword('');
      setConfirming(false);
      setDone({ person: personName(selected) });
    } catch (e) {
      const kind = classifyResetError(e);
      setError(t(`resetPassword.error.${kind}`));
      // Fall back to the form, still with the password in state — it never left.
      setConfirming(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('resetPassword.title', { tenant: tenant.name })}
      className="fixed inset-0 z-50 flex items-stretch justify-center overflow-y-auto bg-black/60 p-0 sm:items-start sm:p-8"
    >
      <div className="w-full min-h-dvh rounded-none border-0 bg-raad-panel p-5 pt-[calc(1.25rem+var(--raad-safe-top))] sm:min-h-0 sm:max-w-lg sm:rounded-2xl sm:border sm:border-raad-border sm:p-6 sm:pt-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[17px] font-semibold">
            {t('resetPassword.title', { tenant: tenant.name })}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-9 rounded-lg border border-raad-border px-3 text-[12.5px] font-semibold"
          >
            {t('resetPassword.close')}
          </button>
        </div>

        {done ? (
          <div>
            <p className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-[12.5px]">
              {t('resetPassword.done', { person: done.person, tenant: tenant.name })}
            </p>
            {/* The two facts the operator must not miss: the user is signed out
                everywhere, and nothing will deliver the new password for them. */}
            <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
              {t('resetPassword.signedOut')}
            </p>
            <p className="mt-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
              {t('resetPassword.tellThem')}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white"
              >
                {t('resetPassword.doneButton')}
              </button>
            </div>
          </div>
        ) : confirming && selected ? (
          <div>
            {/* Names the person AND the tenant, so the operator sees exactly whose
                account this touches before it fires. */}
            <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-[12.5px]">
              {t('resetPassword.confirmBody', {
                person: personName(selected),
                tenant: tenant.name,
              })}
            </p>
            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={reset.isPending}
                className="min-h-10 rounded-lg border border-raad-border px-4 text-[13px] font-semibold disabled:opacity-40"
              >
                {t('resetPassword.back')}
              </button>
              <button
                type="button"
                onClick={() => void submit()}
                disabled={reset.isPending}
                className="min-h-10 rounded-lg bg-raad-error px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {reset.isPending ? t('resetPassword.resetting') : t('resetPassword.confirmButton')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <p className="mb-4 rounded-lg border border-raad-border bg-raad-bg/40 p-3 text-[12px] text-raad-muted">
              {t('resetPassword.intro', { tenant: tenant.name })}
            </p>

            {usersQ.isPending ? (
              <p className="text-sm text-raad-muted" role="status">
                {t('resetPassword.loading')}
              </p>
            ) : usersQ.isError ? (
              <p role="alert" className="text-sm text-raad-error">
                {t('resetPassword.usersError')}
              </p>
            ) : users.length === 0 ? (
              <p className="text-sm text-raad-muted">{t('resetPassword.noUsers')}</p>
            ) : (
              <fieldset className="grid gap-2">
                <legend className="mb-1 text-[12px] font-medium text-raad-muted">
                  {t('resetPassword.pick')}
                </legend>
                {users.map((u) => {
                  const disabled = u.disabledAt !== null;
                  return (
                    <label
                      key={u.id}
                      className={[
                        'flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors',
                        userId === u.id
                          ? 'border-raad-primary/60 bg-raad-primary/10'
                          : 'border-raad-border hover:bg-raad-primary/5',
                      ].join(' ')}
                    >
                      <input
                        type="radio"
                        name="reset-password-user"
                        value={u.id}
                        checked={userId === u.id}
                        onChange={() => setUserId(u.id)}
                        className="mt-1"
                      />
                      <span className="block">
                        <span className="block text-[13.5px] font-medium">
                          {u.name ?? u.phone}
                          {disabled ? (
                            <span className="ms-2 inline-flex rounded-md border border-raad-muted/40 bg-raad-muted/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-muted">
                              {t('resetPassword.disabled')}
                            </span>
                          ) : null}
                        </span>
                        <span className="mt-0.5 block text-[11.5px] text-raad-muted">
                          {t(`resetPassword.role.${u.role}`)}
                          {' · '}
                          <span className="num">{u.phone}</span>
                        </span>
                      </span>
                    </label>
                  );
                })}
              </fieldset>
            )}

            {selected ? (
              <div className="mt-4 block">
                <label className="block">
                  <span className="mb-1 block text-[12px] font-medium text-raad-muted">
                    {t('resetPassword.password')}
                  </span>
                  <input
                    className={INPUT}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                </label>
                <span className="mt-1 block text-[11.5px] text-raad-muted">
                  {t('resetPassword.passwordHint')}
                </span>
              </div>
            ) : null}

            {error ? (
              <p
                role="alert"
                className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-[12.5px]"
              >
                {error}
              </p>
            ) : null}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setConfirming(true);
                }}
                disabled={!ready}
                className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-white disabled:opacity-40"
              >
                {t('resetPassword.review')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
